import { generateJSON, generateText, getSchema, rewriteUnknownContent, type JSONContent, type TextSerializer } from "@tiptap/core";
import { extensions, parseTiptapContent } from "./TipTap";

// TipTap persists content as ProseMirror JSON (TipTap.tsx: editor.getJSON()), so any consumer that
// feeds a description to an LLM must extract prose first — otherwise the model receives raw
// {"type":"doc",...} JSON. We do the extraction with TipTap's own generateText, whose schema is
// derived from the same `extensions` the editor uses (getSchema below). That keeps text extraction
// in lockstep with the document format automatically — no hand-maintained list of node types.

const schema = getSchema(extensions);

// Match how the editor renders these leaves as text: a hard break is a space, an image contributes
// its alt text (skipped when absent/blank). Everything else uses the schema's own text serializers.
const textSerializers: Record<string, TextSerializer> = {
  hardBreak: () => " ",
  image: ({ node }) => {
    const alt = node.attrs.alt;
    return typeof alt === "string" && alt.trim() !== "" ? alt : "";
  },
};

/**
 * Extracts human-readable plain text from TipTap/ProseMirror JSON. Non-JSON input and JSON that
 * isn't a TipTap `doc` are returned unchanged — external clients and legacy stored values may hold
 * bare strings. Empty documents yield an empty string.
 */
export const tiptapToPlainText = (raw: string | null | undefined): string => {
  const source = raw?.trim();
  if (!source) return "";
  const document = knownDocumentOf(source);
  if (document === NOT_A_DOCUMENT) return source;
  return document ? plainTextOf(document) : "";
};

export const tiptapVisibleLength = (raw: string | null | undefined): number => {
  if (!raw) return 0;
  const document = knownDocumentOf(raw);
  if (document === NOT_A_DOCUMENT) return raw.length;
  return document ? typedLengthOf(document) : 0;
};

const NOT_A_DOCUMENT = Symbol("not a document");

const knownDocumentOf = (source: string): JSONContent | null | typeof NOT_A_DOCUMENT => {
  const parsed = parseTiptapContent(source);
  if (typeof parsed !== "object" || parsed === null || (parsed as JSONContent).type !== "doc") {
    return NOT_A_DOCUMENT;
  }

  // Strip nodes/marks a future TipTap upgrade or a not-yet-registered extension might introduce, so
  // the Node.fromJSON inside generateText can't throw on content the schema doesn't know yet.
  const { json, rewrittenContent } = rewriteUnknownContent(parsed as JSONContent, schema);
  rewrittenContent.forEach((c) => warnUnknownNode(c.unsupported));
  return json;
};

const typedLengthOf = (node: JSONContent): number =>
  (node.type === "text" ? (node.text?.length ?? 0) : 0) +
  (node.content ?? []).reduce((sum, child) => sum + typedLengthOf(child), 0);

export const htmlToPlainText = (html: string | null | undefined): string => {
  const source = html?.trim();
  return source ? plainTextOf(generateJSON(source, extensions)) : "";
};

const plainTextOf = (json: JSONContent): string =>
  generateText(json, extensions, { blockSeparator: " ", textSerializers }).replace(/\s+/g, " ").trim();

const warnedUnknownTypes = new Set<string>();
const warnUnknownNode = (type: string): void => {
  if (warnedUnknownTypes.has(type)) return;
  warnedUnknownTypes.add(type);
  console.warn(
    `tiptapToPlainText dropped unsupported ProseMirror node/mark '${type}' before text extraction. ` +
      "If this came from a TipTap upgrade or a newly-installed extension, register it in the editor's extensions.",
  );
};
